import { FC, useContext } from 'react';

import Hyperlink from '@ingka/hyperlink';
import SSRIcon from '@ingka/ssr-icon';
import checkmarkIcon from '@ingka/ssr-icon/paths/checkmark';
import { getLocaleSetting, hasTranslation, t } from '@onlinepayments/payments-library';
import isEqual from 'lodash.isequal';

import FullName from '../../components/ContactInfo/FullName';
import DeliveryInfo from '../../components/DeliveryInfo';
import ServiceOrderListItem from '../../components/OrderItemList/ServiceOrderListItem';
import { Address, Box } from '../../components/toolkit';
import { LineType } from '../../enums/LineType';
import { ServiceType } from '../../enums/ServiceType';
import ContactInfo from '../../interfaces/ContactInfo';
import IOrderDetails from '../../interfaces/OrderDetails';
import { OrderDetailsContext } from '../../OrderDetailsContext';

function showShippingContact(order: IOrderDetails) {
  if (
    order?.deliveryServices?.some(
      ({ serviceType }) =>
        serviceType === ServiceType.PickupPointExternal ||
        serviceType === ServiceType.ClickCollectStore ||
        serviceType === ServiceType.PickupPoint,
    )
  ) {
    return false;
  }

  return !isEqual(order.shippingContact, order.billingContact);
}

const OrderContainer: FC = () => {
  const { order, orderItems } = useContext(OrderDetailsContext);
  const { contactUsUrl, customerServicePhoneNumber } = getLocaleSetting();
  const displayContactsUrl = contactUsUrl && hasTranslation('order.contact_us_text_beforepayment');

  const renderContact = ({
    firstName,
    middleName,
    lastName,
    email,
    mobileNumber,
    businessInfo,
    ...address
  }: ContactInfo) => (
    <div className="text-s">
      {email && <p className="mb-0">{email}</p>}
      {mobileNumber && <p className="mb-0">{mobileNumber}</p>}
      {(email || mobileNumber) && <div className="mb-100" />}
      {firstName && lastName && (
        <p className="mb-0">
          <FullName firstName={firstName} lastName={lastName} middleName={middleName} />
        </p>
      )}
      {businessInfo?.businessName && <p className="mb-0">{businessInfo.businessName}</p>}
      <Address {...address} />
    </div>
  );

  if (!order || !orderItems) return null;

  const serviceItems = orderItems.filter(
    ({ orderLine }) => orderLine?.lineType === LineType.Service,
  );

  return (
    <div className="OrderContainer" data-testid="OrderContainer">
      {!!order.deliveryServices && (
        <Box>
          <DeliveryInfo
            shippingContact={order.shippingContact}
            deliveryServices={order.deliveryServices}
          />
        </Box>
      )}

      {serviceItems.length > 0 && (
        <Box>
          <div className="checkmark-heading">
            <div className="checkmark-circle">
              <SSRIcon paths={checkmarkIcon} />
            </div>
            <strong>{t('order.assembly_service_header').replace(/:$/, '')}</strong>
          </div>
          <ul className="ServiceList" data-testid="serviceList">
            {serviceItems.map((item, index) => (
              <ServiceOrderListItem key={`${item.salesItem.itemKey.itemNo}-${index}`} item={item} />
            ))}
          </ul>
        </Box>
      )}

      <Box>
        <div className="checkmark-heading">
          <div className="checkmark-circle">
            <SSRIcon paths={checkmarkIcon} />
          </div>
          <strong>{t('customer_info_header')}</strong>
        </div>
        {showShippingContact(order) && (
          <div className="mb-100">
            <p className="mb-50 text-bold">{t('order.shipping_contact_header')}</p>
            {renderContact(order.shippingContact)}
          </div>
        )}
        {renderContact(order.billingContact)}

        {(hasTranslation('customer_support_info_text') ||
          displayContactsUrl ||
          customerServicePhoneNumber) && (
          <p className="mt-150 mb-0 text-s" data-testid="supportContainer">
            {hasTranslation('customer_support_info_text') && `${t('customer_support_info_text')} `}
            {displayContactsUrl && (
              <Hyperlink
                data-testid="contactUsUrl"
                text={t('order.contact_us_text_beforepayment')}
                newWindow={true}
                url={contactUsUrl}
              />
            )}
            {!displayContactsUrl && customerServicePhoneNumber && (
              <span data-testid="servicePhoneNumber">{customerServicePhoneNumber}</span>
            )}
          </p>
        )}
      </Box>
    </div>
  );
};

export default OrderContainer;
